/**
 * Canonical Entry Routes per Scope
 * ONE protected entry route per scope (LOCKED)
 * 
 * These routes are the single entry points for each scope after authentication.
 * All routes are protected and trigger AuthInitializer for Phase-3 bootstrap.
 */
export const ENTRY_ROUTES = {
  PLATFORM: '/platform/dashboard',
  ORGANIZATION: '/org/dashboard',
  BRANCH: '/branch/dashboard', // branchSlug is added by UrlGenerator.getBranchUrl(), not in template
} as const

/**
 * Build branch entry route with actual slugs
 * Note: orgSlug is in the subdomain (e.g., qubicgen.staging.vidhyantra.com)
 * branchSlug is also in the path, but it's added by UrlGenerator.getBranchUrl()
 * 
 * @param _orgSlug - Organization slug (used for subdomain, not path - kept for API compatibility)
 * @param _branchSlug - Branch slug (added by UrlGenerator.getBranchUrl, not here)
 * @returns Branch entry route path (WITHOUT branchSlug prefix)
 */
export function buildBranchEntryRoute(_orgSlug: string, _branchSlug: string): string {
  // Path structure: /branch/dashboard
  // UrlGenerator.getBranchUrl will construct: https://orgSlug.domain.com/{branchSlug}{path}
  // So we only return the route part, not the branchSlug prefix
  return `/branch/dashboard`
}

/**
 * Get entry route for context type
 * @param contextType - Context type (platform, organization, branch)
 * @param orgSlug - Organization slug (required for branch context)
 * @param branchSlug - Branch slug (required for branch context)
 * @returns Entry route path for the context
 */
export function getEntryRouteForContext(
  contextType: 'platform' | 'organization' | 'branch',
  orgSlug?: string,
  branchSlug?: string
): string {
  switch (contextType) {
    case 'platform':
      return ENTRY_ROUTES.PLATFORM
    case 'organization':
      return ENTRY_ROUTES.ORGANIZATION
    case 'branch':
      if (!orgSlug || !branchSlug) {
        throw new Error('Branch entry route requires orgSlug and branchSlug')
      }
      return buildBranchEntryRoute(orgSlug, branchSlug)
    default:
      throw new Error(`Unknown context type: ${contextType}`)
  }
}

